<script setup lang="ts">
import { type AnchorHTMLAttributes, computed, useAttrs } from 'vue'

import BaseButton, { type BaseButtonProps } from './BaseButton.vue'

type BaseButtonInherits = Omit<BaseButtonProps, 'component' | 'type'>
export type ExternalLinkButtonProps = /* @vue-ignore */ AnchorHTMLAttributes & BaseButtonInherits

const props = defineProps<ExternalLinkButtonProps>()
const attrs = useAttrs()

defineSlots<InstanceType<typeof BaseButton>['$slots']>()

defineOptions({
  inheritAttrs: false,
})

/** Auto-add rel="noopener noreferrer" for target="_blank" (security best practice) */
const rel = computed(() => {
  // target/rel are attrs at runtime (not extracted from AnchorHTMLAttributes)
  if (attrs.rel) return attrs.rel
  if (attrs.target === '_blank') return 'noopener noreferrer'
  return undefined
})

/**
 * Explicit rebind — Vue 3.5 resolves BaseButton props on this component at runtime,
 * so they no longer fall through. Mirror RouterLinkButton.
 *
 * Anchor `type` (MIME) is wider than BaseButton's button `type` union; cast for <a> use.
 */
const buttonBind = computed(() => {
  const { type: _propType, ...buttonProps } = props
  return {
    ...buttonProps,
    ...attrs,
    component: 'a' as const,
    type: attrs.type as BaseButtonProps['type'],
    rel: rel.value,
  }
})
</script>

<template>
  <BaseButton v-bind="buttonBind">
    <template v-for="(_, name) in $slots" #[name]>
      <slot :name="name" />
    </template>
  </BaseButton>
</template>
